/**
 * Module registry — top-level modules with their sub-items.
 * Each module gets its own workspace (color, sub-sidebar).
 *
 * `planned: true` marks a module whose backend workflows haven't shipped yet.
 * Its landing page renders a "Planned module — not yet operational" banner
 * and all submodule routes point at ModuleStub. Wire real pages
 * incrementally per the M1..M6 roadmap in /app/memory/PRD.md.
 */
import {
  ShoppingBag, Utensils, ScrollText, Grid3x3, LayoutDashboard,
  Database, Package, Tags, Building2, Receipt, CreditCard,
  Wallet, Users, CalendarClock, ClipboardCheck, FileBarChart, Banknote,
  CalendarDays, ListChecks, UserPlus, PiggyBank,
  Ticket, TicketPercent, UsersRound, Megaphone, TrendingUp,
  ShieldCheck, ScanLine, Fingerprint, Ban, Activity,
  Boxes, ArrowUpDown, TriangleAlert, Warehouse,
  Coins, HandCoins, LockKeyhole, CheckCircle2,
  PieChart, BarChart3, DollarSign, PercentSquare, Footprints,
  UserCog, Shield, Send, KeyRound, ScrollText as AuditIcon,
  Settings2,
  // Club-ops expansion icons
  ClipboardList, DoorOpen, NotebookPen, ArrowLeftRight, CheckSquare,
  Truck, ShoppingCart, Handshake, FileText, ClipboardX,
  Star, Award, MessageSquare, HeartHandshake, IdCard, Gift, Users2,
  Sparkles, CalendarRange, Radio, LineChart, Hash as HashIcon,
  Wrench, Hammer, AlertTriangle, MapPin, Search, Zap, ShieldAlert,
  FileCheck, FileClock, ShieldQuestion, Archive,
} from "lucide-react";

export const MODULES = [
  // 01 · Point of Sale
  {
    key: "pos",
    name: "Point of Sale",
    tagline: "Orders, floor & kitchen",
    description: "Take orders, route tickets to bar and kitchen, manage tables and payments.",
    icon: ShoppingBag,
    accent: "violet",
    basePath: "/pos",
    permission: "order.view",
    items: [
      { label: "POS Terminal",   path: "/pos/terminal", icon: Grid3x3,     perm: "order.create" },
      { label: "KDS / Bar",      path: "/pos/kds",      icon: Utensils,    perm: "kds.view" },
      { label: "Live Orders",    path: "/pos/orders",   icon: ScrollText,  perm: "order.view" },
      { label: "Floor & Tables", path: "/pos/tables",   icon: LayoutDashboard, perm: "dashboard.view" },
    ],
  },
  // 02 · Host · Tonight
  {
    key: "host",
    name: "Host · Tonight",
    tagline: "Arrivals · seating",
    description: "Host quick view — check-in confirmations from Security, table assignment, Arrive & Seat.",
    icon: UserCog,
    accent: "indigo",
    basePath: "/work/host",
    permission: "reservation.manage",
    hideSidebar: true,
    items: [
      { label: "Tonight", path: "/work/host", icon: UserCog, perm: "reservation.manage", end: true },
    ],
  },
  // 03 · Operations Center — planned
  {
    key: "operations",
    name: "Operations Center",
    tagline: "MOD dashboard · shifts · handover",
    description: "One place for the Manager on Duty to open, run, and hand over the venue each day. Summarizes Finance close-out, Attendance and Entrance without duplicating them.",
    icon: ClipboardList,
    accent: "amber",
    basePath: "/operations",
    permission: "operations.view",
    planned: true,
    items: [
      { label: "MOD Dashboard",  path: "/operations",           icon: LayoutDashboard, perm: "operations.view", end: true, planned: true },
      { label: "Opening",        path: "/operations/opening",   icon: DoorOpen,        perm: "operations.open_close", planned: true },
      { label: "Closing",        path: "/operations/closing",   icon: LockKeyhole,     perm: "operations.open_close", planned: true },
      { label: "Shifts",         path: "/operations/shifts",    icon: CalendarClock,   perm: "operations.view", planned: true },
      { label: "Tasks",          path: "/operations/tasks",     icon: CheckSquare,     perm: "operations.task_manage", planned: true },
      { label: "Logbook",        path: "/operations/logbook",   icon: NotebookPen,     perm: "operations.manage", planned: true },
      { label: "Handover",       path: "/operations/handover",  icon: ArrowLeftRight,  perm: "operations.handover", planned: true },
      { label: "Readiness",      path: "/operations/readiness", icon: ShieldCheck,     perm: "operations.view", planned: true },
    ],
  },
  // 04 · Reservations
  {
    key: "reservations",
    name: "Reservations",
    tagline: "Bookings & deposits",
    description: "Reservation calendar, customer bookings, deposits, QR check-in.",
    icon: CalendarDays,
    accent: "pink",
    basePath: "/reservations",
    permission: "reservation.manage",
    items: [
      { label: "Calendar",       path: "/reservations",           icon: CalendarDays,  perm: "reservation.manage", end: true },
      { label: "All Bookings",   path: "/reservations/list",      icon: ListChecks,    perm: "reservation.manage" },
      { label: "Customers",      path: "/reservations/customers", icon: UserPlus,      perm: "reservation.manage" },
      { label: "VIP Packages",   path: "/reservations/vip-packages", icon: Star,       perm: "reservation.manage" },
      { label: "Deposits",       path: "/reservations/deposits",  icon: PiggyBank,     perm: "reservation.manage" },
    ],
  },
  // 05 · Guests & Loyalty — planned
  {
    key: "guests",
    name: "Guests & Loyalty",
    tagline: "Profiles · VIP · rewards",
    description: "One unified guest identity across Reservations, POS, Events and Entrance. Reuses the existing customer records — no parallel database.",
    icon: Users2,
    accent: "rose",
    basePath: "/guests",
    permission: "guest.view",
    planned: true,
    items: [
      { label: "Overview",         path: "/guests",              icon: Users2,         perm: "guest.view", end: true, planned: true },
      { label: "Profiles",         path: "/guests/profiles",     icon: IdCard,         perm: "guest.view", planned: true },
      { label: "Visits",           path: "/guests/visits",       icon: Footprints,     perm: "guest.view", planned: true },
      { label: "Segments",         path: "/guests/segments",     icon: Sparkles,       perm: "guest.view", planned: true },
      { label: "VIP",              path: "/guests/vip",          icon: Star,           perm: "guest.vip_manage", planned: true },
      { label: "Loyalty",          path: "/guests/loyalty",      icon: Award,          perm: "guest.loyalty_manage", planned: true },
      { label: "Memberships",      path: "/guests/memberships",  icon: TicketPercent,  perm: "guest.membership_manage", planned: true },
      { label: "Vouchers",         path: "/guests/vouchers",     icon: Gift,           perm: "guest.loyalty_manage", planned: true },
      { label: "Feedback",         path: "/guests/feedback",     icon: MessageSquare,  perm: "guest.feedback_manage", planned: true },
      { label: "Service Recovery", path: "/guests/service-recovery", icon: HeartHandshake, perm: "guest.service_recovery", planned: true },
    ],
  },
  // 06 · Events & Promoters
  {
    key: "events",
    name: "Events & Promoters",
    tagline: "Tickets & guest lists",
    description: "Events, ticketed nights, guest lists, promoter portal and commissions.",
    icon: Ticket,
    accent: "fuchsia",
    basePath: "/events",
    permission: "event.manage",
    items: [
      { label: "Events",       path: "/events",             icon: Ticket,         perm: "event.manage", end: true },
      { label: "Promoters",    path: "/events/promoters",   icon: Megaphone,      perm: "event.manage" },
    ],
  },
  // 07 · Marketing & Campaigns — planned
  {
    key: "marketing",
    name: "Marketing & Campaigns",
    tagline: "Campaigns · offers · attribution",
    description: "Plan promotions, target audiences, publish content and track attribution. Campaign rules define eligibility — POS remains the source of truth for applying discounts.",
    icon: Megaphone,
    accent: "fuchsia",
    basePath: "/marketing",
    permission: "marketing.view",
    planned: true,
    items: [
      { label: "Overview",     path: "/marketing",              icon: Megaphone,     perm: "marketing.view", end: true, planned: true },
      { label: "Calendar",     path: "/marketing/calendar",     icon: CalendarRange, perm: "marketing.view", planned: true },
      { label: "Campaigns",    path: "/marketing/campaigns",    icon: TrendingUp,    perm: "marketing.manage", planned: true },
      { label: "Offers",       path: "/marketing/offers",       icon: TicketPercent, perm: "marketing.offer_manage", planned: true },
      { label: "Vouchers",     path: "/marketing/vouchers",     icon: Gift,          perm: "marketing.offer_manage", planned: true },
      { label: "Audiences",    path: "/marketing/audiences",    icon: UsersRound,    perm: "marketing.audience_view", planned: true },
      { label: "Content",      path: "/marketing/content",      icon: FileText,      perm: "marketing.content_manage", planned: true },
      { label: "Channels",     path: "/marketing/channels",     icon: Radio,         perm: "marketing.manage", planned: true },
      { label: "Influencers",  path: "/marketing/influencers",  icon: Handshake,     perm: "marketing.manage", planned: true },
      { label: "Referrals",    path: "/marketing/referrals",    icon: HashIcon,      perm: "marketing.manage", planned: true },
      { label: "Attribution",  path: "/marketing/attribution",  icon: LineChart,     perm: "marketing.analytics", planned: true },
      { label: "Performance",  path: "/marketing/performance",  icon: BarChart3,     perm: "marketing.analytics", planned: true },
    ],
  },
  // 08 · Entrance & Security
  {
    key: "security",
    name: "Entrance & Security",
    tagline: "Door · scan · occupancy",
    description: "Universal door check-in — scan tickets, reservations, guest lists, walk-ins from a single input.",
    icon: Shield,
    accent: "red",
    basePath: "/security",
    permission: "entrance.scan",
    hideSidebar: true,
    items: [
      { label: "Door", path: "/security", icon: Activity, perm: "foot_traffic.view", end: true },
    ],
  },
  // 09 · Inventory
  {
    key: "inventory",
    name: "Inventory",
    tagline: "Stock & wastage",
    description: "Stock items, balances, movements, counts and wastage tracking.",
    icon: Boxes,
    accent: "amber",
    basePath: "/inventory",
    permission: "inventory.view",
    items: [
      { label: "Overview",     path: "/inventory",             icon: Boxes,          perm: "inventory.view", end: true },
      { label: "Items",        path: "/inventory/items",       icon: Package,        perm: "inventory.view" },
      { label: "Balances",     path: "/inventory/balances",    icon: Warehouse,      perm: "inventory.view" },
      { label: "Movements",    path: "/inventory/movements",   icon: ArrowUpDown,    perm: "inventory.view" },
      { label: "Wastage",      path: "/inventory/wastage",     icon: TriangleAlert,  perm: "inventory.adjust" },
      { label: "Stock Counts", path: "/inventory/counts",      icon: ClipboardCheck, perm: "inventory.adjust" },
    ],
  },
  // 10 · Purchasing & Suppliers — planned
  {
    key: "purchasing",
    name: "Purchasing & Suppliers",
    tagline: "PR → PO → Receiving",
    description: "Complete the flow between Inventory and Finance: supplier directory, purchase requests, approvals, POs, receiving and payables. Never duplicates the inventory master or approval engine.",
    icon: Truck,
    accent: "blue",
    basePath: "/purchasing",
    permission: "purchasing.view",
    planned: true,
    items: [
      { label: "Overview",        path: "/purchasing",                icon: Truck,           perm: "purchasing.view", end: true, planned: true },
      { label: "Suppliers",       path: "/purchasing/suppliers",      icon: Building2,       perm: "supplier.manage", planned: true },
      { label: "Purchase Requests", path: "/purchasing/requests",     icon: FileText,        perm: "purchasing.request", planned: true },
      { label: "Purchase Orders", path: "/purchasing/orders",         icon: ShoppingCart,    perm: "purchasing.order", planned: true },
      { label: "Receiving",       path: "/purchasing/receiving",      icon: ClipboardCheck,  perm: "purchasing.receive", planned: true },
      { label: "Returns",         path: "/purchasing/returns",        icon: ClipboardX,      perm: "purchasing.return", planned: true },
      { label: "Price History",   path: "/purchasing/price-history",  icon: TrendingUp,      perm: "supplier.price_view", planned: true },
      { label: "Contracts",       path: "/purchasing/contracts",      icon: Handshake,       perm: "supplier.manage", planned: true },
      { label: "Payables",        path: "/purchasing/payables",       icon: HandCoins,       perm: "purchasing.view", planned: true },
    ],
  },
  // 11 · Facilities, Maintenance & Incidents — planned
  {
    key: "facilities",
    name: "Facilities & Incidents",
    tagline: "Assets · maintenance · incidents",
    description: "Assets, work orders, incident reports, inspections, cleaning and lost-and-found. Physical entrance admission stays in Entrance & Security.",
    icon: Wrench,
    accent: "orange",
    basePath: "/facilities",
    permission: "facility.view",
    planned: true,
    items: [
      { label: "Overview",     path: "/facilities",              icon: Wrench,          perm: "facility.view", end: true, planned: true },
      { label: "Assets",       path: "/facilities/assets",       icon: Boxes,           perm: "facility.asset_manage", planned: true },
      { label: "Maintenance",  path: "/facilities/maintenance",  icon: Hammer,          perm: "facility.work_order_manage", planned: true },
      { label: "Work Orders",  path: "/facilities/work-orders",  icon: ClipboardList,   perm: "facility.work_order_manage", planned: true },
      { label: "Inspections",  path: "/facilities/inspections",  icon: ClipboardCheck,  perm: "facility.inspection_manage", planned: true },
      { label: "Incidents",    path: "/facilities/incidents",    icon: ShieldAlert,     perm: "facility.incident_view", planned: true },
      { label: "Lost & Found", path: "/facilities/lost-found",   icon: Search,          perm: "facility.lost_found_manage", planned: true },
      { label: "Cleaning",     path: "/facilities/cleaning",     icon: Sparkles,        perm: "facility.inspection_manage", planned: true },
      { label: "Utilities",    path: "/facilities/utilities",    icon: Zap,             perm: "facility.view", planned: true },
    ],
  },
  // 12 · Finance & Cash
  {
    key: "finance",
    name: "Finance & Cash",
    tagline: "Cash, expenses, closing",
    description: "Cash sessions, expenses, end-of-day closing and approvals.",
    icon: Coins,
    accent: "green",
    basePath: "/finance",
    permission: "cash_session.manage",
    items: [
      { label: "Overview",       path: "/finance",             icon: Coins,        perm: "cash_session.manage", end: true },
      { label: "Cash Sessions",  path: "/finance/cash",        icon: HandCoins,    perm: "cash_session.manage" },
      { label: "Expenses",       path: "/finance/expenses",    icon: Receipt,      perm: "cash_session.manage" },
      { label: "Daily Closing",  path: "/finance/closing",     icon: LockKeyhole,  perm: "cash_session.manage" },
      { label: "Approvals",      path: "/finance/approvals",   icon: CheckCircle2, perm: "approval.manage" },
    ],
  },
  // 13 · HR & Payroll
  {
    key: "payroll",
    name: "HR & Payroll",
    tagline: "Employees & pay runs",
    description: "Employees, schedules, attendance, payroll periods and payslips.",
    icon: Wallet,
    accent: "emerald",
    basePath: "/payroll",
    permission: "payroll.view",
    items: [
      { label: "Payroll Dashboard", path: "/payroll",             icon: BarChart3,        perm: "payroll.view", end: true },
      { label: "Employees",         path: "/payroll/employees",   icon: Users,            perm: "payroll.view" },
      { label: "Schedules",         path: "/payroll/schedules",   icon: CalendarClock,    perm: "attendance.manage" },
      { label: "Attendance",        path: "/payroll/attendance",  icon: ClipboardCheck,   perm: "attendance.manage" },
      { label: "Payroll Periods",   path: "/payroll/periods",     icon: FileBarChart,     perm: "payroll.prepare" },
      { label: "Payslips",          path: "/payroll/payslips",    icon: Banknote,         perm: "payroll.view" },
    ],
  },
  // 14 · Master Files
  {
    key: "masterfiles",
    name: "Master Files",
    tagline: "Catalog, venue, config",
    description: "Products, categories, tables, branches, tax and payment methods.",
    icon: Database,
    accent: "blue",
    basePath: "/masterfiles",
    permission: "product.manage",
    items: [
      { label: "Products",         path: "/masterfiles/products",   icon: Package,     perm: "product.manage" },
      { label: "Categories",       path: "/masterfiles/categories", icon: Tags,        perm: "product.manage" },
      { label: "Tables & Floors",  path: "/masterfiles/venue",      icon: Grid3x3,     perm: "table.manage" },
      { label: "Branches",         path: "/masterfiles/branches",   icon: Building2,   perm: "branch.manage" },
      { label: "Payment Methods",  path: "/masterfiles/payments",   icon: CreditCard,  perm: "branch.manage" },
      { label: "Tax & Charges",    path: "/masterfiles/tax",        icon: Receipt,     perm: "branch.manage" },
    ],
  },
  // 15 · Reports & Insights
  {
    key: "reports",
    name: "Reports & Insights",
    tagline: "Command center & KPIs",
    description: "Owner command center, sales, product mix, foot traffic and payroll summaries. Future: ops readiness, purchasing, guest retention, campaign attribution, maintenance and incident trends.",
    icon: PieChart,
    accent: "rose",
    basePath: "/reports",
    permission: "dashboard.view",
    items: [
      { label: "Command Center",   path: "/reports",             icon: LayoutDashboard, perm: "dashboard.view", end: true },
      { label: "Daily Sales",      path: "/reports/sales",       icon: DollarSign,       perm: "dashboard.view" },
      { label: "Products",         path: "/reports/products",    icon: Package,          perm: "dashboard.view" },
      { label: "Payments",         path: "/reports/payments",    icon: CreditCard,       perm: "dashboard.view" },
      { label: "Discounts & Voids",path: "/reports/discounts",   icon: PercentSquare,    perm: "dashboard.view" },
      { label: "Foot Traffic",     path: "/reports/foot",        icon: Footprints,       perm: "foot_traffic.view" },
      { label: "Payroll Summary",  path: "/reports/payroll",     icon: BarChart3,        perm: "payroll.view" },
    ],
  },
  // 16 · Team & Access
  {
    key: "team",
    name: "Team & Access",
    tagline: "Users, roles, sessions",
    description: "Invite users, manage roles, view sessions, revoke access.",
    icon: UserCog,
    accent: "cyan",
    basePath: "/team",
    permission: "user.manage",
    items: [
      { label: "Users",         path: "/team",           icon: Users,        perm: "user.manage", end: true },
      { label: "Roles Matrix",  path: "/team/roles",     icon: ShieldCheck,  perm: "user.manage" },
      { label: "Invitations",   path: "/team/invites",   icon: Send,         perm: "user.manage" },
      { label: "Sessions",      path: "/team/sessions",  icon: KeyRound,     perm: "user.manage" },
    ],
  },
  // 17 · Audit & Compliance (extended with planned submodules)
  {
    key: "audit",
    name: "Audit & Compliance",
    tagline: "Activity · permits · privacy",
    description: "Append-only audit trail today. Roadmap adds business permits, liquor & sanitary licenses, inspections, document expiry alerts, privacy reviews and retention policies.",
    icon: AuditIcon,
    accent: "zinc",
    basePath: "/audit",
    permission: "audit_log.view",
    items: [
      { label: "Activity Log", path: "/audit",             icon: AuditIcon,       perm: "audit_log.view", end: true },
      { label: "Permits",      path: "/audit/permits",     icon: FileCheck,       perm: "audit_log.view", planned: true },
      { label: "Licenses",     path: "/audit/licenses",    icon: FileCheck,       perm: "audit_log.view", planned: true },
      { label: "Inspections",  path: "/audit/inspections", icon: ClipboardCheck,  perm: "audit_log.view", planned: true },
      { label: "Documents",    path: "/audit/documents",   icon: FileText,        perm: "audit_log.view", planned: true },
      { label: "Privacy",      path: "/audit/privacy",     icon: ShieldQuestion,  perm: "audit_log.view", planned: true },
      { label: "Retention",    path: "/audit/retention",   icon: Archive,         perm: "audit_log.view", planned: true },
    ],
  },
  // 18 · Settings & Configuration
  {
    key: "settings",
    name: "Settings & Configuration",
    tagline: "Formulas, thresholds, rules",
    description: "Tune every business rule the app enforces — tax, service charge, approval thresholds, KDS aging, entrance policy, payroll multipliers, nightly cutoff. Future categories: operations, purchasing, guests, loyalty, marketing, facilities, compliance.",
    icon: Settings2,
    accent: "zinc",
    basePath: "/settings",
    permission: "settings.manage",
    items: [
      { label: "All Settings", path: "/settings", icon: Settings2, perm: "settings.manage", end: true },
    ],
  },
];

/** Monochrome accent tokens — every module uses the same charcoal + white
 *  vocabulary. Distinction comes from icons, section numbers and typography
 *  rather than color, per the minimalist reference design. */
const MONO = {
  text: "text-foreground",
  bg: "bg-foreground/[0.05]",
  border: "border-border",
  ring: "ring-foreground/25",
  solid: "bg-foreground text-background",
  soft: "bg-foreground/[0.04]",
  glow: "shadow-[0_30px_60px_-30px_hsl(var(--foreground)/0.35)]",
};
export const ACCENTS = {
  violet: MONO, blue: MONO, emerald: MONO, pink: MONO, fuchsia: MONO,
  red: MONO, amber: MONO, green: MONO, rose: MONO, cyan: MONO, zinc: MONO,
  indigo: MONO, orange: MONO,
};

export const moduleByKey = (key) => MODULES.find((m) => m.key === key);
